import { useState, MouseEvent } from 'react';

import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import classNames from 'classnames';

import { Icon } from '../../Icon';
import { Box } from '../../Box';
import { Typography } from '../../Typography';
import { Badge } from '../../Badge';
import { EllipsisText } from '../../EllipsisText';

import useStyles from './DropdownInput.styles';

/**
 * Возможные размеры выпадающего списка выбора.
 */
export type DropdownSize = 'large' | 'small';

/**
 * Свойства компонента {@link DropdownInput}
 *
 * @typeParam T - Тип значения, который может быть строкой или числом.
 */
interface DropdownInputProps<T> {
  /**
   * Массив элементов для отображения в выпадающем меню.
   * Каждый элемент должен содержать метку (`label`) и значение (`value`), опционально может быть указано, является ли элемент активным (`active`).
   */
  items: {
    label: string;
    value: T;
    active?: boolean;
  }[];

  /**
   * Текущее выбранное значение или массив значений. Может быть null, если ничего не выбрано.
   */
  value: T | T[] | null;

  /**
   * Функция, вызываемая при изменении выбранного значения.
   * Принимает новое значение в качестве аргумента.
   */
  onChange: (value: T) => void;

  /**
   * Опциональный ID для тестирования компонента.
   */
  testId?: string;

  /**
   * Размер выпадающего списка выбора.
   */
  size?: DropdownSize;
}

/**
 * Компонент для создания выпадающего списка выбора.
 *
 * @public
 * @typeParam T - Тип значения, который может быть строкой или числом.
 * @param items - Массив элементов для отображения в выпадающем меню.
 * @param value - Текущее выбранное значение или массив значений.
 * @param onChange - Функция, вызываемая при изменении выбранного значения.
 * @param testId - Опциональный ID для тестирования компонента.
 * @param size - Размер выпадающего списка выбора.
 * @returns Возвращает JSX-элемент представляющий компонент DropdownInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link DropdownInputProps}
 */
export const DropdownInput = <T extends string | number>({
  value,
  items,
  testId,
  onChange,
  size = 'small',
}: DropdownInputProps<T>) => {
  const { classes } = useStyles();

  const selectedOption = items.find((option) => option.value === value);
  const hasActiveOptions = items.some((item) => !!item.active);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Box
        className={classNames(classes.dropdown, 'aqa_dropdown_input')}
        display="flex"
        alignItems="center"
        onClick={handleClick}
        gap="8"
        testId={testId}
      >
        <Typography
          variant={size === 'small' ? 'h6' : 'h5'}
          component="div"
          noWrap
        >
          {selectedOption ? selectedOption.label : '-'}
        </Typography>

        {items.length > 1 && (
          <Icon name="dropdown" color="original.brand.primary" size="24" />
        )}

        {hasActiveOptions && (
          <Box
            display="flex"
            alignSelf="flex-start"
            width={6}
            height="100%"
            ml="-8"
            position="relative"
          >
            <Badge visible={hasActiveOptions} />
          </Box>
        )}
      </Box>
      {items.length > 1 && (
        <Menu
          elevation={0}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          classes={{
            list: classes.menuList,
            paper: classes.menuPaper,
          }}
        >
          {items.map((item) => (
            <MenuItem
              value={item.value}
              key={item.value}
              onClick={() => {
                onChange(item.value);
                handleClose();
              }}
              className={classes.menuItem}
            >
              <Typography
                color={
                  item.value === value
                    ? 'original.brand.primary'
                    : 'text.light.primary'
                }
                variant="body3"
              >
                <EllipsisText rows={1}>{item.label}</EllipsisText>
              </Typography>
              {item.active && (
                <Box display="flex" height={18} width={12} justifyContent="end">
                  <Box
                    width={6}
                    height={6}
                    bgcolor="accent.text.error"
                    radius="round"
                  />
                </Box>
              )}
            </MenuItem>
          ))}
        </Menu>
      )}
    </div>
  );
};
